import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import TNEDashboardHeader from '../../common/TNEDashboardHeader/TNEDashboardHeader';
import { deleteTimesheet, getNextTimesheets, getTimesheets } from '../../../actions/timesheets';
import CustomTable from '../../common/Table/CustomTable/CustomTable';
import { columns } from './helpers';
import {
  globalPageSize,
  responsiveSizeTimesheet,
  contractorFilters,
  approverFilters,
  appAreas,
  appNotificationsTypes
} from '../../../utils/constants';
import { useOnScreen, usePrevious, useQuery, useWindowSize } from '../../../utils/hooks';
import TNECardsList from '../../common/TNECardsList/TNECardsList';
import AddGradientIcon from '../../common/Icons/AddGradientIcon';
import StyledPaginationPanel from '../../common/StyledPaginationPanel/StyledPaginationPanel';
import classes from './Timesheets.module.scss';
import MainButton from '../../common/buttons/MainButton';
import { clearTimesheet, getTimeExpenseValidation, getTandEPendingCounts } from '../../../actions/timesheet';
import { basicErrorHandler, checkRoleInLS } from '../../../utils/helpers';
import colors from '../../../scss/colors';
import TnEListSkeleton from '../../common/Skeletons/TnEListSkeleton';
import { getOptionsForFilters } from '../../../actions/filtersOptionsFromApi';
import WithCloseIcon from '../../common/ComposableWrappers/WithCloseIcon/WithCloseIcon';
import BaseModal from '../../common/ModalWindows/BaseModal';
import { CopyModal, DeletePopUp } from '../timesheet/components/modals/modals';
import { removeNotification, setNotification } from '../../../actions/appNotifications';
import { tneAxiosClient } from '../../../api/apiSettings';
import EmptyRecord from '../../common/EmptyRecordComponent/EmptyRecord';
import TimesheetEmptyIcon from '../../common/Icons/TimesheetEmptyIcon';

export default function Timesheets() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const size = useWindowSize();
  const query = useQuery();

  const initIntersactionRequestRef = useRef(true);
  const IntersactionMarkerRef = useRef(null);
  const { isIntersecting } = useOnScreen(IntersactionMarkerRef, {
    threshold: 0.1
  });

  const timesheetsList = useSelector((state) => state.timesheets.data);
  const paginationData = useSelector((state) => state.timesheets.pagination);
  const infinityIsLoading = useSelector((state) => state.timesheets.pendingInfinityScroll);
  const timesheetsPagination = useSelector((state) => state.timesheets.pagination);
  const timesheetsPending = useSelector((state) => state.timesheets.pending);
  const deletePending = useSelector((state) => state.timesheets.pendingDelete);
  const isApprover = useSelector((state) => state.accessToParts.tAndEApprover);
  const timesheetValidationStatus = useSelector((state) => state?.timesheet?.validate?.timesheet);
  const filtersOptions = useSelector((state) => state.filtersOptionsFromApi.filters);
  const tsCounts = isApprover
    ? {
        counter: 1
        /* TODO need BE to rework response for GET v1/get-tne-count the same as for GET v1/get-tne-count-contractor */
      }
    : useSelector((state) => state.timesheet.contractortandecounts?.Timesheet);
  const filters = isApprover ? approverFilters : contractorFilters;
  const initPageNumber = Number(query.get('page')) || 1;
  const initStartDate = query.get('start') || '';
  const initMonth = query.get('month') || '';
  const initYear = query.get('year') || '';
  const initLocation = query.get('location') || '';
  const initStatus = query.get('status') || '';
  const initFirstName = query.get('FirstName') || '';
  const initLastName = query.get('LastName') || '';

  const [active, setCurrentPage] = useState(initPageNumber);
  const [start, setStart] = useState(initStartDate);
  const [month, setMonth] = useState(initMonth);
  const [year, setYear] = useState(initYear);
  const [location, setLocation] = useState(initLocation);
  const [status, setStatus] = useState(initStatus);
  const [contractor, setContractor] = useState({
    FirstName: initFirstName,
    LastName: initLastName
  });
  const [openPopUp, setOpenPop] = useState(false);
  const [tsFordelete, setTSfordelete] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const prevPendingDelete = usePrevious(deletePending);
  const browserLocation = useLocation();
  const itemClickHandler = (id) => navigate(`/dashboard/timesheet/${id}${browserLocation.search}`);
  const userId = localStorage.getItem('UserId');

  const handleSearchParams = () => {
    let oldSearchParams = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of searchParams.entries()) {
      oldSearchParams = {
        ...oldSearchParams,
        [key]: value
      };
    }

    return setSearchParams({
      ...oldSearchParams,
      page: active,
      start,
      month,
      year,
      location,
      status,
      FirstName: contractor.FirstName,
      LastName: contractor.LastName
    });
  };

  const deleteHandler = (id) => {
    setOpenPop({ modalType: 'delete' });
    setTSfordelete(id);
  };

  const finalDeletingHandler = () => {
    dispatch(deleteTimesheet(tsFordelete));
  };

  const copyHandler = (id) => {
    setOpenPop({ modalType: 'copy', params: { timesheet: timesheetsList.find((ts) => ts.Id === id) } });
  };

  const handlePagination = (num) => {
    setCurrentPage(num);
  };

  const handleMonth = (choosedMonth) => {
    setMonth(choosedMonth);
    if (choosedMonth === t('common.general.all')) setYear('');
    if (choosedMonth && choosedMonth !== t('common.general.all')) setStart('');
  };

  const handleYear = (choosedYear) => {
    setYear(choosedYear);
  };

  const handleWeek = (choosedStart) => {
    setStart(choosedStart);
    if (choosedStart) handleMonth('');
  };

  const handleLocation = (choosedLocation) => {
    setLocation(choosedLocation);
  };

  const handleStatus = (choosedStatus) => {
    setStatus(choosedStatus);
  };

  const handleContractor = (choosedContractor) => {
    setContractor(choosedContractor);
  };

  useEffect(() => {
    dispatch(
      getTimesheets(
        userId,
        active,
        globalPageSize,
        start,
        month,
        year,
        location,
        status,
        contractor,
        t,
        isApprover || checkRoleInLS('tAndEApprover'),
        filtersOptions
      )
    );
    handleSearchParams();
  }, [active, start, month, year, location, status, contractor]);

  useEffect(() => {
    dispatch(clearTimesheet());
    dispatch(getTimeExpenseValidation('', ''));
  }, []);

  useEffect(() => {
    if (isApprover) {
      dispatch(getTandEPendingCounts());
    }
    dispatch(getOptionsForFilters(appAreas.TIMESHEETS, isApprover));
  }, [isApprover, timesheetsList]);

  useEffect(() => {
    if (
      isIntersecting &&
      size.width < responsiveSizeTimesheet &&
      !initIntersactionRequestRef.current &&
      paginationData &&
      paginationData.PageNumber + 1 <= paginationData?.TotalPages
    ) {
      dispatch(
        getNextTimesheets(
          userId,
          Number(paginationData.PageNumber) + 1,
          globalPageSize,
          start,
          month,
          year,
          location,
          status,
          contractor,
          t,
          isApprover || checkRoleInLS('tAndEApprover'),
          filtersOptions
        )
      );
    }
    initIntersactionRequestRef.current = false;
  }, [isIntersecting]);

  useEffect(() => {
    if ((prevPendingDelete && !deletePending) || browserLocation.state?.idToDelete) {
      setOpenPop(false);
      dispatch(
        setNotification({
          isAppStyled: true,
          message: `${t('dashboard.timesheet.timesheet')} ${t('common.general.deleted')}`,
          duration: 3000,
          type: appNotificationsTypes.SUCCESS,
          bottom: '160px',
          action: (
            <p
              role="presentation"
              className={classes.undoBtn}
              onClick={async () => {
                try {
                  // implement call to API to restore timesheet by id using tsFordelete
                  await tneAxiosClient.get(
                    `/v1/timesheet/activate?timesheetId=${browserLocation.state?.idToDelete || tsFordelete}`
                  );
                  dispatch(
                    getTimesheets(
                      userId,
                      active,
                      globalPageSize,
                      start,
                      month,
                      year,
                      location,
                      status,
                      contractor,
                      t,
                      isApprover || checkRoleInLS('tAndEApprover'),
                      filtersOptions
                    )
                  );
                  dispatch(removeNotification());
                } catch (e) {
                  basicErrorHandler(dispatch, e.message);
                }
              }}
            >
              {t('common.general.undo')}
            </p>
          )
        })
      );
    }
  }, [prevPendingDelete, deletePending]);

  const popUpModals = {
    copy: <CopyModal timesheet={openPopUp?.params?.timesheet} withExternalRedirect={false} />,
    delete: (
      <DeletePopUp
        deleteHandler={finalDeletingHandler}
        closeHandler={() => setOpenPop(false)}
        withBackbtn={false}
        pending={deletePending}
      />
    )
  };

  if (!timesheetsPending && !Object.values(tsCounts)?.some((counter) => counter > 0))
    return (
      <EmptyRecord
        title={t('dashboard.timesheets.mobileTitle')}
        icon={<TimesheetEmptyIcon />}
        headerLabel={t('dashboard.createtimesheet.emptytimesheet')}
        startTracking={
          !isApprover
            ? t('dashboard.createtimesheet.starttrackingnow')
            : t('dashboard.createtimesheet.emptynotification')
        }
        mainButtonLabel={!isApprover ? t('dashboard.timesheets.newbuttontxt') : t('common.general.goback')}
        mainButtonHandler={!isApprover ? '/dashboard/create-timesheet' : '/dashboard'}
        textButtonLabel={t('dashboard.createtimesheet.gotomaindashboard')}
        textButtonHandler="/dashboard"
        isApprover={isApprover}
      />
    );

  return (
    <section className={classes.mainContainer}>
      <TNEDashboardHeader
        title={size.width < responsiveSizeTimesheet ? t('dashboard.timesheets.mobileTitle') : ''}
        filters={filters}
        values={{
          start,
          month,
          year,
          location,
          status,
          contractor
        }}
        changeHandlers={{
          handleWeek,
          handleMonth,
          handleYear,
          handleLocation,
          handleStatus,
          handleContractor
        }}
      />
      {size.width > responsiveSizeTimesheet ? (
        <CustomTable
          data={timesheetsList || []}
          columns={() => columns(t, navigate, copyHandler, deleteHandler, isApprover)}
          withFooter={false}
          withLines={{
            header: false,
            body: false,
            footer: false
          }}
          bodyWithBorderRadius={false}
          rowClickHandler={itemClickHandler}
          wrapperStyles={{}}
          headerStyles={{
            borderRadius: '10px  10px 10px 10px',
            padding: '20px 40px 20px 88px'
          }}
          bodyStyles={{
            overflow: 'visible'
          }}
          lineStyles={{
            backgroundColor: colors.WHITE,
            boxShadow: '0 0 30px rgba(61, 107, 149, 0.1)',
            borderRadius: '10px',
            height: '68px',
            marginTop: '12px',
            padding: '20px 40px 20px 88px'
          }}
          isLoading={timesheetsPending}
          loadingComponent={<TnEListSkeleton qty={6} height="68px" gap="12px" />}
        />
      ) : (
        <div className={classes.cardListWrapper}>
          <TNECardsList
            type="timesheet"
            data={timesheetsList}
            clickHandler={itemClickHandler}
            isLoading={timesheetsPending}
            loadingComponent={<TnEListSkeleton qty={10} height="90px" gap="16px" />}
          />
        </div>
      )}
      <div ref={IntersactionMarkerRef}>
        {infinityIsLoading && <CircularProgress sx={{ color: colors.NAVY_BLUE }} />}
      </div>
      {!isApprover && (
        <div className={classes.addButtonContainer}>
          <Link to="/dashboard/create-timesheet">
            <div className={classes.innerContainer}>
              {size.width > responsiveSizeTimesheet ? (
                <MainButton
                  rounded
                  forceWidth="max-content"
                  size="medium"
                  variant={timesheetValidationStatus ? 'primary' : 'disabled'}
                  disabled={!timesheetValidationStatus}
                >
                  {t('dashboard.timesheets.newbuttontxt')}
                </MainButton>
              ) : (
                <AddGradientIcon size="50px" />
              )}
            </div>
          </Link>
        </div>
      )}
      {size.width > responsiveSizeTimesheet && (
        <div className={`${classes.paginationPanelContainer} ${isApprover ? classes.withApprover : ''}`}>
          <StyledPaginationPanel
            total={timesheetsPagination?.TotalPages}
            active={active}
            setActive={(num) => handlePagination(num)}
          />
        </div>
      )}
      <BaseModal
        open={!!openPopUp}
        fullscreen={false}
        closeHandler={() => setOpenPop(null)}
        title="&nbsp;"
        hideFooter
        hideHeader
        withSeparator={false}
        marginLeftRight={3}
        backgroundColor={colors.PAGE_BACKGROUND}
      >
        <WithCloseIcon close={() => setOpenPop(null)}>{popUpModals[openPopUp?.modalType]}</WithCloseIcon>
      </BaseModal>
    </section>
  );
}
